import React, {useEffect, useMemo, useRef, useState} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FiMaximize2, FiMinimize2, FiFilter, FiColumns } from 'react-icons/fi'
import { useAuth0 } from '@auth0/auth0-react'
import { useLocation } from 'react-router-dom'

import DateRangeComponent from '../CommonComponents/DateRangeComponent'

import MyCampaignTable from '../MyCampaignTable'
import ProductTableComponent from '../ProductTableComponent'
import PortfolioTableComponent from '../PortfolioTableComponent'
import TagTableComponent from '../TagTableComponent'
import RuleTableComponent from '../RuleTableComponent'
import BudgetRecommendationTable from '../BudgetRecommendationTable'
import DspComponent from '../DspComponent'

import { ReactComponent as TaskOnSvg } from '../../assets/svg/task.svg'
import { ReactComponent as TaskOffSvg } from '../../assets/svg/task-passive.svg'

import {
  getExportKeywords,
  getKeywords,
} from '../../redux/actions/keyword'

import { toggleDashboardTable } from '../../redux/actions/dashboard'
import { setDateRange } from '../../redux/actions/header'
import { resetAppState } from '../../redux/actions/appState'
import {
  showFilter,
  showColumnEditorAction,
} from '../../redux/actions/pageGlobal'
import { keywordStatuses } from '../../utils/filterDef'
import { selectIsNonEndemicAccount } from '../../redux/reducers/header'
import TargetOpKeywordTable from '../BulkOp/TargetOpKeywordTable'

const TAB_CAMPAIGN = 'campaign'
const TAB_PRODUCT = 'product'
const TAB_BUDGET = 'budget'
const TAB_DSP = 'dsp'
const TAB_ALL_KEYWORDS = 'all_keywords'
export const TAB_PORTFOLIO = 'portfolio'
export const TAB_TAG = 'tag'
export const TAB_RULE = 'rule'

const tabList = [
  { tab: TAB_CAMPAIGN, name: 'My Campaigns' },
  { tab: TAB_PRODUCT, name: 'My Products' },
  { tab: TAB_BUDGET, name: 'Budgets' },
  { tab: TAB_DSP, name: 'DSP' },
  { tab: TAB_ALL_KEYWORDS, name: 'All Keywords' },
]

const filterNameList = {
  [TAB_CAMPAIGN]: 'campaignTable',
  [TAB_PRODUCT]: 'productTable',
}

const TableCollection = () => {
  const dispatch = useDispatch()
  const { getAccessTokenSilently } = useAuth0()
  const location = useLocation()

  const isNonEndemicAccount = useSelector(selectIsNonEndemicAccount)
  const maxTable = useSelector(state => state.dashboard.maxTable)
  const isLoadingKeywords = useSelector(state => state.keyword.isLoadingKeywords)
  const isLoadingExportKeywords = useSelector(state => state.keyword.isLoadingExportKeywords)
  const currentStartDate = useSelector(state => state.header.currentStartDate)
  const currentEndDate = useSelector(state => state.header.currentEndDate)
  const accountList = useSelector(state => state.header.accountList)
  const filterValues = useSelector(state => state.pageGlobal.filterValues)

  const [activeTab, setActiveTab] = useState(TAB_CAMPAIGN)
  const [isShowNotedCampaigns, setIsShowNotedCampaigns] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState(keywordStatuses[0])
  const [pageNum, setPageNum] = useState(1)
  const [pageRows, setPageRows] = useState(10)
  const [keywordSearchValue, setKeywordSearchValue] = useState('')

  const abortControllerRef = useRef(null)
  const exportAbortControllerRef = useRef(null)

  // Clear previous page data when entering campaigns page to prevent memory leaks
  useEffect(() => {
    if (location.pathname.includes('/campaigns')) {
      dispatch(resetAppState())
    }
  }, [dispatch, location.pathname])

  const tabsToRender = useMemo(() => {
    const hasDspAccount = accountList.find(account =>
      account.seller_type === 'agency' && account.sub_type === 'dsp'
    )
    let filtered
    if (hasDspAccount) {
      filtered = tabList
    } else {
      filtered = tabList.filter(tab => tab.tab !== TAB_DSP)
    }

    if (isNonEndemicAccount) {
      filtered = tabList.filter(tab =>
        tab.tab !== TAB_PRODUCT && tab.tab !== TAB_PORTFOLIO
      )
    }

    return filtered
  }, [accountList, isNonEndemicAccount])

  const loadKeywordsData = async (
    startDate,
    endDate,
    pageNumParam = 1,
    pageRowsParam = 10,
    keywordSearchValue = '',
    filterValues,
    signal // Add signal parameter
  ) => {
    try {
      const accessToken = await getAccessTokenSilently()

      // Check if aborted before making the call
      if (signal?.aborted) {
        return
      }

      dispatch(getKeywords(
        accessToken,
        startDate,
        endDate,
        pageNumParam,
        pageRowsParam,
        keywordSearchValue,
        filterValues,
        signal // Add signal parameter
      ))
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to load keywords:', error)
      }
    }
  }

  useEffect(() => {
    if (activeTab === TAB_ALL_KEYWORDS) {

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController()

      loadKeywordsData(
        currentStartDate,
        currentEndDate,
        pageNum,
        pageRows,
        keywordSearchValue,
        filterValues,
        abortControllerRef.current.signal
      )
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      if (exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort()
      }
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentStartDate, currentEndDate, pageNum, pageRows, filterValues, keywordSearchValue])

  const loadDataByPagination = (pageNumParam) => {
    setPageNum(pageNumParam)
  }

  const onKeywordSearchChange = (searchText) => {
    setPageNum(1)
    setKeywordSearchValue(searchText)
  }

  const getExportsDataFromServer = async () => {
    if (exportAbortControllerRef.current) {
      exportAbortControllerRef.current.abort()
    }

    // Create new abort controller
    exportAbortControllerRef.current = new AbortController()
    const signal = exportAbortControllerRef.current.signal
    try {
      const accessToken = await getAccessTokenSilently()

      // Check if aborted before making the call
      if (signal?.aborted) {
        return []
      }

      const exportData = await dispatch(getExportKeywords(
        accessToken,
        currentStartDate,
        currentEndDate,
        filterValues,
        signal // Add signal parameter
      ))

      return exportData || []
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to load export keywords:', error)
      }
      return []
    }
  }

  const onRowsPerPageChange = (newPageRows) => {
    setPageRows(newPageRows)
  }

  const handleDateRangeChange = ([ startDate, endDate ]) => {
    dispatch(setDateRange({
      startDate,
      endDate,
    }))
  }

  const handleFilterShow = () => {
    dispatch(showFilter(filterNameList[activeTab]))
  }

  const handleColumnEditorShow = () => {
    dispatch(showColumnEditorAction())
  }

  const renderMaxMin = () => {
    if (!maxTable) {
      return (
        <FiMaximize2
          color='#979797'
          onClick={() => { dispatch(toggleDashboardTable()) }}
        />
      )
    }
    return (
      <FiMinimize2
        color='#979797'
        onClick={() => { dispatch(toggleDashboardTable()) }}
      />
    )
  }

  const renderContents = () => {
    if (activeTab === TAB_CAMPAIGN) {
      return (
        <MyCampaignTable
          showNotedCampaigns={isShowNotedCampaigns}
          onChangeViewMethod={setActiveTab}
        />
      )
    }
    if (activeTab === TAB_PRODUCT) {
      return <ProductTableComponent />
    }
    if (activeTab === TAB_PORTFOLIO) {
      return <PortfolioTableComponent />
    }
    if (activeTab === TAB_TAG) {
      return <TagTableComponent />
    }
    if (activeTab === TAB_BUDGET) {
      return <BudgetRecommendationTable />
    }
    if (activeTab === TAB_RULE) {
      return <RuleTableComponent />
    }
    if (activeTab === TAB_DSP) {
      return <DspComponent />
    }
    if (activeTab === TAB_ALL_KEYWORDS) {
      return (
        <div className="all-keywords-container">
          <TargetOpKeywordTable
            sortBy="cost"
            sourceView="command-center"
            selectedStatus={selectedStatus}
            onSelectStatus={setSelectedStatus}
            isLoadingKeywords={isLoadingKeywords}
            isLoadingExportKeywords={isLoadingExportKeywords}
            loadDataByPagination={loadDataByPagination}
            onRowsPerPageChange={onRowsPerPageChange}
            getExportsDataFromServer={getExportsDataFromServer}
            onSearchChange={onKeywordSearchChange}
          />
        </div>
      )
    }
    return null
  }

  const renderAction = () => (
    <>
      {
        (activeTab === TAB_CAMPAIGN || activeTab === TAB_PRODUCT) && (
          <FiFilter
            title="Filter"
            color="#979797"
            onClick={handleFilterShow}
          />
        )
      }
      {
        activeTab === TAB_CAMPAIGN && (
          isShowNotedCampaigns ? (
            <TaskOnSvg
              title="Show Noted Campaigns"
              onClick={() => { setIsShowNotedCampaigns(false) }}
            />
          ) : (
            <TaskOffSvg
              title="Show Noted Campaigns"
              onClick={() => { setIsShowNotedCampaigns(true) }}
            />
          )
        )
      }
      {
        (activeTab === TAB_CAMPAIGN || activeTab === TAB_PRODUCT) && (
          <FiColumns
            title="Column Customizer"
            color="#979797"
            onClick={handleColumnEditorShow}
          />
        )
      }
    </>
  )

  return (
    <div className="table-collection">
      <div className="table-tabs">
        <div className="table-tab-left">
          {
            tabsToRender.map(tab => {
              let tabName = tab.name
              if ((activeTab === TAB_PORTFOLIO || activeTab === TAB_RULE || activeTab === TAB_TAG)
                && tab.tab === TAB_CAMPAIGN) {
                tabName = 'Back To My Campaigns'
              }
              return (
                <button
                  key={tab.tab}
                  type="button"
                  className={`table-tab${activeTab === tab.tab ? ' selected' : ''}`}
                  onClick={() => { setActiveTab(tab.tab) }}
                >
                  { tabName }
                </button>
              )
            })
          }
        </div>
        <div className="table-tab-right">
          {
            activeTab !== TAB_RULE && (
              <>
                <DateRangeComponent
                  value={[currentStartDate, currentEndDate]}
                  placement="bottomEnd"
                  onChange={handleDateRangeChange}
                />
                { renderAction() }
              </>
            )
          }
          { renderMaxMin() }
        </div>
      </div>
      { renderContents() }
    </div>
  )
}

export default TableCollection
